import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { CustomError, PageResponse } from '@/slw/shared/types/api.type';

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  errorHandling?: CustomError;
}

export class ApiCall {
  private api: AxiosInstance;

  constructor(api: AxiosInstance, attachApiInterceptor?: () => void) {
    if (attachApiInterceptor) attachApiInterceptor();

    this.api = api;
  }

  async get<TResult = unknown>(
    endpoint: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .get(endpoint, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async post<TResult = unknown, TData = Record<string, unknown>>(
    endpoint: string,
    data?: TData,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .post(endpoint, data, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async put<TResult = unknown, TData = Record<string, unknown>>(
    endpoint: string,
    data?: TData,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .put(endpoint, data, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async patch<TResult = unknown, TData = Record<string, unknown>>(
    endpoint: string,
    data?: TData,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .patch(endpoint, data, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async delete<TResult = unknown>(
    endpoint: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .delete(endpoint, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async download<TResult = unknown, TData = FormData>(
    endpoint: string,
    data?: TData,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    const options: AxiosRequestConfig = {
      responseType: config?.responseType ?? 'blob',
    };

    return new Promise((resolve, reject) => {
      this.api
        .post(
          endpoint,
          data,
          signal ? (config ? { ...config, signal, ...options } : { signal, ...options }) : { ...config, ...options },
        )
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async upload<TResult = unknown, TData = FormData>(
    endpoint: string,
    data?: TData,
    config?: CustomAxiosRequestConfig,
  ): Promise<TResult extends Blob ? AxiosResponse<TResult> : PageResponse<TResult>> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    return new Promise((resolve, reject) => {
      this.api
        .post(endpoint, data, signal ? (config ? { ...config, signal } : { signal }) : config)
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async downloadFile(
    endpoint: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<Blob> {
    let signal: AbortSignal | null = null;

    if (!config?.signal) {
      const controller = new AbortController();

      signal = controller.signal;
    }

    const options: AxiosRequestConfig = {
      responseType: 'blob',
    };

    return new Promise((resolve, reject) => {
      this.api
        .get(
          endpoint,
          signal ? (config ? { ...config, signal, ...options } : { signal, ...options }) : { ...config, ...options },
        )
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
